import React, { useState, useEffect, useMemo } from "react";
import { Box, IconButton, Avatar, Drawer, useMediaQuery, Button } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import Sidebar from "./Sidebar/Sidebar";
import ChatViewArea from "./ChatViewArea";
import MessageInput from "./MessageInput";
import WelcomeScreen from "./WelcomeScreen";
import AccountMenu from "../Menu/AccountMenu";
import ModelSelectionMenu from "../Menu/ModelSelectionMenu";
import DatabaseSelectionMenu from "../Menu/DatabaseSelectionMenu";
import StyleSelectionMenu from "../Menu/StyleSelectionMenu";
import { EditChatDialog } from "../Dialogs/EditChatDialog";
import { DeleteChatDialog } from "../Dialogs/DeleteChatDialog";
import { FeedbackChatDialog } from "../Dialogs/FeedbackChatDialog";
import { FeedbackMsgDialog } from "../Dialogs/FeedbackMsgDialog";
import { useChat } from "../../hooks/useChat";
import { useAuth } from "../../hooks/useAuth";
import { useAssistance } from "../../hooks/useAssistance";
import { authService } from "../../services/authService";
import { DialogState } from "../../types";
import AppBackground from "../Layout/AppBackground";

interface ChatLayoutProps {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  themeMode: string;
  setThemeMode: (newMode: "light" | "dark" | "auto") => void;
  fixedDatabase?: string;
  isWidget?: boolean;
}

const ChatLayout: React.FC<ChatLayoutProps> = ({
  sidebarOpen: sidebarOpenProp,
  toggleSidebar: toggleSidebarProp,
  themeMode,
  setThemeMode,
  fixedDatabase,
  isWidget,
}) => {
  const [widgetSidebarOpen, setWidgetSidebarOpen] = useState(false);
  const sidebarOpen = isWidget ? widgetSidebarOpen : sidebarOpenProp;
  const toggleSidebar = isWidget ? () => setWidgetSidebarOpen(prev => !prev) : toggleSidebarProp;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const {
    activeChat,
    setActiveChat,
    chats,
    messages,
    setMessages,
    isGenerating,
    setIsGenerating,
    isLoading,
    setIsLoading,
    isLoadingMessages,
    models,
    selectedModel,
    handleSelectModel,
    isLoadingModel,
    databases,
    selectedDatabase,
    handleSelectDatabase,
    isLoadingDatabase,
    styles,
    selectedStyle,
    handleSelectStyle,
    isLoadingStyle,
    hasMoreChats,
    hasMoreMessages,
    handleLoadMoreChats,
    handleLoadMoreMessages,
    fetchChats,
    sendMessage,
    createNewChat,
    cancelChat,
    editChat,
    feedbackChat,
    deleteChat,
    suggestionPrompt,
  } = useChat(fixedDatabase);
  const { assistanceMode, setAssistanceMode, assistanceOptions } =
    useAssistance();
  const { logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const authData = authService.getAuthData();
  const userInfo = authData?.user_info;

  const avatarInitials = useMemo(() => {
    if (!userInfo?.name) return "?";
    return userInfo.name
      .split(" ")
      .map((part: string) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }, [userInfo?.name]);

  const [accountAnchorEl, setAccountAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [modelAnchorEl, setModelAnchorEl] = useState<null | HTMLElement>(null);
  const [databaseAnchorEl, setDatabaseAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [styleAnchorEl, setStyleAnchorEl] = useState<null | HTMLElement>(null);
  const [dialogState, setDialogState] = useState<DialogState>({
    type: null,
    isOpen: false,
    chatId: null,
    msgId: null,
  });
  const [newChatName, setNewChatName] = useState<string>("");

  const handleOpenDialog = (
    type: "edit" | "feedback" | "feedbackmsg" | "delete",
    chatId: string,
    msgId?: string
  ) => {
    setDialogState({ type, isOpen: true, chatId, msgId: msgId ? msgId : null });
    if (type === "edit") {
      const chat = chats.find((c) => c.id === chatId);
      setNewChatName(chat?.name || "");
    }
  };

  const handleCloseDialog = () => {
    setDialogState({ type: null, isOpen: false, chatId: null, msgId: null });
    setNewChatName("");
  };

  const handleEditChat = async () => {
    if (!dialogState.chatId || !newChatName.trim()) return;

    try {
      await editChat(dialogState.chatId, newChatName.trim());
      setNewChatName("");
    } catch (err) {
      console.error("Error updating chat name:", err);
    } finally {
      handleCloseDialog();
    }
  };

  const handleFeedbackChat = async (rating: number, comment: string) => {
    if (!dialogState.chatId) return;

    try {
      await feedbackChat(dialogState.chatId, rating, comment);
    } catch (err) {
      console.error("Error submitting feedback:", err);
    } finally {
      handleCloseDialog();
    }
  };

  const handleFeedbackMsg = async (
    rating: number,
    comment: string,
    msgId: string | null
  ) => {
    if (!dialogState.chatId || !dialogState.msgId) return;

    try {
      await feedbackChat(
        dialogState.chatId,
        rating,
        "MessageID: " + msgId + " " + comment
      );
    } catch (err) {
      console.error("Error submitting feedback:", err);
    } finally {
      handleCloseDialog();
    }
  };

  const handleDeleteChat = async () => {
    if (!dialogState.chatId) return;

    try {
      await deleteChat(dialogState.chatId);
    } catch (err) {
      console.error("Error deleting chat:", err);
    } finally {
      handleCloseDialog();
    }
  };

  const onSendMessage = async (message: string) => {
    try {
      if (!activeChat) {
        await createNewChat(message, assistanceMode, navigate);
      } else {
        await sendMessage(activeChat, message, assistanceMode);
      }
    } catch (err) {
      console.error("Error in message processing:", err);
    }
  };

  const basePath = fixedDatabase ? "/dms" : "/chat";
  const hideDatabaseSelector = !!fixedDatabase;

  const handleNewChat = () => {
    setActiveChat(null);
    setMessages([]);
    navigate(`${basePath}/new`);
    if (isMobile) {
      toggleSidebar();
    }
  };

  const handleSelectChat = (chatId: string | null) => {
    setActiveChat(chatId);
    if (isMobile) {
      toggleSidebar();
    }
  }

  const handleAccountClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    setAccountAnchorEl(event.currentTarget);
  const handleAccountClose = () => setAccountAnchorEl(null);

  const handleModelClick = (event: React.MouseEvent<HTMLElement>) => setModelAnchorEl(event.currentTarget);
  const handleModelClose = () => setModelAnchorEl(null);
  const handleModelChange = async (model: string) => {
      try {
          handleSelectModel(model);
          handleModelClose();
      } catch (error) {
          console.error("Error updating model:", error);
      }
  };

  const handleDatabaseClick = (event: React.MouseEvent<HTMLElement>) => setDatabaseAnchorEl(event.currentTarget);
  const handleDatabaseClose = () => setDatabaseAnchorEl(null);
  const handleDatabaseChange = (database: string) => {
      handleSelectDatabase(database);
      handleDatabaseClose();
  };

  const handleStyleClick = (event: React.MouseEvent<HTMLElement>) => setStyleAnchorEl(event.currentTarget);
  const handleStyleClose = () => setStyleAnchorEl(null);
  const handleStyleChange = (style: string) => {
      handleSelectStyle(style);
      handleStyleClose();
  };

  const handleCancelChat = () => {
      if (activeChat) {
          cancelChat(activeChat);
      }
  };

  const handleRelatedQuestionClick = async (question: string) => {
      setIsGenerating(true);
      try {
          if (!activeChat) {
              await createNewChat(question, assistanceMode, navigate);
          } else {
              await sendMessage(activeChat, question, assistanceMode);
          }
      } catch (err) {
          console.error("Error sending related question:", err);
      } finally {
          setIsGenerating(false);
      }
  };

  useEffect(() => {
    fetchChats();
  }, []);

  useEffect(() => {
    if (id && chats.some((chat) => chat.id === id)) {
      setActiveChat(id);
    }
  }, [id, chats, setActiveChat]);

  const sidebarContent = (
    <Sidebar
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      chats={chats}
      activeChat={activeChat}
      setActiveChat={handleSelectChat}
      onEditChat={(chatId) => handleOpenDialog("edit", chatId)}
      onFeedbackChat={(chatId) => handleOpenDialog("feedback", chatId)}
      onDeleteChat={(chatId) => handleOpenDialog("delete", chatId)}
      isGenerating={isGenerating}
      isLoading={isLoading}
      onNewChat={handleNewChat}
      onLoadMore={handleLoadMoreChats}
      hasMore={hasMoreChats}
    />
  );

  return (
    <Box
      display="flex"
      height="100vh"
      sx={{
        overflow: "hidden",
        position: "relative",
      }}
    >
      <AppBackground />

      {(isMobile || isWidget) ? (
        <Drawer
          variant="temporary"
          open={sidebarOpen}
          onClose={toggleSidebar}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
              boxSizing: 'border-box',
              background: 'rgba(255,255,255,0.7)',
              backdropFilter: 'blur(20px)',
              border: 'none',
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      ) : (
        <Box
          component="aside"
          sx={{
            width: sidebarOpen ? 260 : 0,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            boxSizing: 'border-box',
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
            background: 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(20px)',
            borderRight: '1px solid rgba(255,255,255,0.3)',
          }}
        >
          {sidebarContent}
        </Box>
      )}

      {/* This Box is now just a positioning container */}
      <Box
        component="main"
        flex={1}
        sx={{
          position: "relative",
          overflow: activeChat ? "hidden" : "auto",
        }}
      >
        <Button
          onClick={toggleSidebar}
          startIcon={<MenuOutlinedIcon />}
          aria-label="Toggle sidebar"
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            zIndex: 10,
            height: 38,
            borderRadius: '12px',
            color: 'text.primary',
            background: alpha(theme.palette.background.default, 0.6),
            backdropFilter: 'blur(8px)',
            '&:hover': {
              background: alpha(theme.palette.background.default, 0.8),
              borderColor: alpha(theme.palette.divider, 0.6),
            }}}>
          ประวัติการพูดคุย
        </Button>

        {!isWidget && (
          <Avatar
            component="button"
            onClick={handleAccountClick}
            aria-label="Account menu"
            sx={{
              cursor: "pointer",
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 10,
              fontSize: 16,
              width: 38,
              height: 38,
              borderRadius: '12px',
              color: 'text.primary',
              background: alpha(theme.palette.background.default, 0.6),
              backdropFilter: 'blur(8px)',
              '&:hover': {
                background: alpha(theme.palette.background.default, 0.8),
                borderColor: alpha(theme.palette.divider, 0.6),
              }
            }}
          >
            {avatarInitials}
          </Avatar>
        )}

        {activeChat ? (
          <>
            <Box sx={{
              position: 'absolute',
              top: isWidget ? 0 : '64px',
              bottom: 0,
              left: 0,
              right: 0,
              display: 'flex',
              flexDirection: 'column',
            }}>
              <ChatViewArea
                activeChatId={activeChat}
                messages={messages}
                isGenerating={isGenerating}
                isLoadingMessages={isLoadingMessages}
                hasMoreMessages={hasMoreMessages}
                onFeedbackMessage={(chatId, msgId) =>
                  handleOpenDialog("feedbackmsg", chatId, msgId)
                }
                onRelatedQuestionClick={handleRelatedQuestionClick}
                handleLoadMoreMessages={handleLoadMoreMessages}
                sidebarOpen={sidebarOpen}
                models={models}
                databases={databases}
                styles={styles}
                assistanceMode={assistanceMode}
                onSendMessage={onSendMessage}
              />
            </Box>

            {/* Wrapper for MessageInput, also absolutely positioned */}
            <Box sx={{ 
              position: 'absolute', 
              bottom: 0, 
              left: 0, 
              right: 0,
              zIndex: 2 
            }}>
              <MessageInput
                onSendMessage={onSendMessage}
                onCancel={handleCancelChat}
                isGenerating={isGenerating}
                models={models}
                selectedModel={selectedModel}
                handleModelClick={handleModelClick}
                databases={databases}
                selectedDatabase={selectedDatabase}
                handleDatabaseClick={handleDatabaseClick}
                styles={styles}
                selectedStyle={selectedStyle}
                handleStyleClick={handleStyleClick}
                mode="chat"
                suggestionPrompt={suggestionPrompt}
                hideDatabaseSelector={hideDatabaseSelector}
              />
            </Box>
          </>
        ) : (
          <WelcomeScreen
            onSendMessage={onSendMessage}
            isGenerating={isGenerating}
            onCancel={handleCancelChat}
            models={models}
            selectedModel={selectedModel}
            handleModelClick={handleModelClick}
            databases={databases}
            selectedDatabase={selectedDatabase}
            handleDatabaseClick={handleDatabaseClick}
            styles={styles}
            selectedStyle={selectedStyle}
            handleStyleClick={handleStyleClick}
            suggestionPrompt={suggestionPrompt}
            hideDatabaseSelector={hideDatabaseSelector}
            isWidget={isWidget}
          />
        )}
      </Box>

      {/* All Menus and Dialogs remain unchanged */}
      <AccountMenu
        anchorEl={accountAnchorEl}
        open={Boolean(accountAnchorEl)}
        onClose={handleAccountClose}
        themeMode={themeMode}
        setThemeMode={setThemeMode}
        onLogout={logout}
      />
      <ModelSelectionMenu
        anchorEl={modelAnchorEl}
        open={Boolean(modelAnchorEl)}
        onClose={handleModelClose}
        models={models}
        selectedModel={selectedModel}
        onModelSelect={handleModelChange}
        isLoadingModel={isLoadingModel}
      />
      <DatabaseSelectionMenu
        anchorEl={databaseAnchorEl}
        open={Boolean(databaseAnchorEl)}
        onClose={handleDatabaseClose}
        databases={databases}
        selectedDatabase={selectedDatabase}
        onDatabaseSelect={handleDatabaseChange}
        isLoadingDatabase={isLoadingDatabase}
      />
      <StyleSelectionMenu
        anchorEl={styleAnchorEl}
        open={Boolean(styleAnchorEl)}
        onClose={handleStyleClose}
        styles={styles}
        selectedStyle={selectedStyle}
        onStyleSelect={handleStyleChange}
        isLoadingStyle={isLoadingStyle}
      />
      <EditChatDialog
        open={dialogState.type === "edit" && dialogState.isOpen}
        onClose={handleCloseDialog}
        onSave={handleEditChat}
        chatName={newChatName}
        onChatNameChange={setNewChatName}
      />
      <FeedbackChatDialog
        open={dialogState.type === "feedback" && dialogState.isOpen}
        onClose={handleCloseDialog}
        onSubmit={handleFeedbackChat}
      />
      <FeedbackMsgDialog
        open={dialogState.type === "feedbackmsg" && dialogState.isOpen}
        onClose={handleCloseDialog}
        onSubmit={handleFeedbackMsg}
        msgId={dialogState.msgId}
      />
      <DeleteChatDialog
        open={dialogState.type === "delete" && dialogState.isOpen}
        onClose={handleCloseDialog}
        onConfirm={handleDeleteChat}
      />
    </Box>
  );
};

export default ChatLayout;